import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const PARENTESCOS_LABELS = {
  partner: "Pareja",
  parent: "Progenitor",
  child: "Hijo/a",
  sibling: "Hermano/a",
  grandparent: "Abuelo/a",
  grandchild: "Nieto/a",
  guardian: "Tutor legal",
  family: "Familiar",
};

export default function Solicitudes() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("pending");
  const { reloadNotifications } = useAuth();

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/solicitudes/admin");
      setRequests(data);
      await reloadNotifications();
    } catch (err) {
      toast.error("No se pudieron cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (id, action) => {
    try {
      await api.post(`/solicitudes/${id}/${action}`);
      toast.success(
        action === "accept" ? "Solicitud aprobada" : "Solicitud rechazada",
      );
      load();
    } catch (err) {
      toast.error("No se pudo resolver");
    }
  };

  const filteredRequests = requests.filter((r) => r.estado === statusTab);

  return (
    <div className="p-6 md:p-10">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
          Gestión
        </div>

        <h1 className="font-display text-4xl font-bold text-med-text">
          Solicitudes
        </h1>

        <p className="text-med-muted mt-2">
          Gestiona solicitudes familiares pendientes y resueltas.
        </p>
      </header>

      {/* TABS */}
      <div className="flex flex-wrap gap-2 mb-8">
        {[
          {
            key: "pending",
            label: "Pendientes",
          },
          {
            key: "accepted",
            label: "Aceptadas",
          },
          {
            key: "rejected",
            label: "Rechazadas",
          },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setStatusTab(t.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              statusTab === t.key
                ? "bg-med-primary text-white"
                : "bg-med-surface border border-med-border text-med-text hover:border-med-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-med-muted">Cargando solicitudes...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="bg-med-surface border border-med-border rounded-2xl p-6 text-med-muted">
            No hay solicitudes.
          </div>
        ) : (
          filteredRequests.map((r) => (
            <motion.div
              key={r.id}
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="bg-med-surface border border-med-border rounded-2xl p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        r.tipo === "delete"
                          ? "bg-red-100 text-red-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {r.tipo === "delete"
                        ? "Eliminar relación"
                        : "Nueva relación"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-med-text">
                      {r.solicitante?.nombre} {r.solicitante?.apellidos}
                    </span>

                    <span className="text-med-muted">→</span>

                    <span className="font-semibold text-med-text">
                      {r.objetivo?.nombre} {r.objetivo?.apellidos}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-med-surface2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-med-muted">
                      {PARENTESCOS_LABELS[r.parentesco]}
                    </span>
                    {r.estado === "pending" && (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                        Pendiente
                      </span>
                    )}
                    {r.estado === "accepted" && (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Aceptada
                      </span>
                    )}
                    {r.estado === "rejected" && (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                        Rechazada
                      </span>
                    )}
                  </div>
                </div>

                {r.estado === "pending" && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => resolve(r.id, "accept")}
                      className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      Aprobar
                    </button>

                    <button
                      onClick={() => resolve(r.id, "reject")}
                      className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                    >
                      Rechazar
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
